const searchForms = document.querySelectorAll('.header__search');
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_DELAY = 250;
const DISALLOWED_SEARCH_CHARS = /[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ\s]/g;

const sanitizeSearchValue = (value) => (value || '')
    .replace(DISALLOWED_SEARCH_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();

searchForms.forEach((form) => {
    const header = form.closest('.header');
    const mobileOverlayShell = header ? header.querySelector('.header__mobile-overlay-shell') : null;
    const menuToggle = header ? header.querySelector('.header__menu-toggle') : null;
    const input = form.querySelector('.header__search-input');
    const searchButton = form.querySelector('.header__search-button');
    const showAllLink = form.querySelector('.header__search-all');
    const keywordList = form.querySelector('.header__search-menu-list--keywords');
    const suggestionList = form.querySelector('.header__search-menu-list--suggestions');
    const suggestionsUrl = form.dataset.searchSuggestionsUrl;
    const fallbackKeywords = keywordList ? keywordList.innerHTML : '';
    const fallbackSuggestions = suggestionList ? suggestionList.innerHTML : '';
    const mobileQuery = window.matchMedia('(max-width: 1024px)');
    let debounceTimer = null;
    let activeRequest = null;

    if (!input) {
        return;
    }

    const searchResultsUrl = form.getAttribute('action') || 'search-results.html';

    const getSearchValue = () => sanitizeSearchValue(input.value);

    const getSearchUrl = () => `${searchResultsUrl}?q=${encodeURIComponent(getSearchValue())}`;

    const getSuggestionsUrl = () => {
        const url = suggestionsUrl.replace('{query}', '');
        return url.replace(/[?&]q=$/, '');
    };

    const getAntiForgeryToken = () => (
        form.querySelector('input[name="__RequestVerificationToken"]')?.value
        || document.querySelector('input[name="__RequestVerificationToken"]')?.value
        || ''
    );

    const createListItem = (item) => {
        const listItem = document.createElement('li');
        const link = document.createElement('a');
        const text = typeof item === 'string' ? item : item.text || item.title;
        const url = typeof item === 'string' ? `${searchResultsUrl}?q=${encodeURIComponent(item)}` : item.url;

        link.href = url || `${searchResultsUrl}?q=${encodeURIComponent(text || getSearchValue())}`;
        link.textContent = text || '';
        listItem.appendChild(link);

        return listItem;
    };

    const renderList = (list, items, fallback, useFallback = true) => {
        if (!list) {
            return;
        }

        const title = list.previousElementSibling;
        const isTitle = title && title.classList.contains('header__search-menu-title');

        if (!Array.isArray(items) || items.length === 0) {
            list.innerHTML = useFallback ? fallback : '';
            if (isTitle && !list.innerHTML.trim()) {
                title.style.display = 'none';
            } else if (isTitle) {
                title.style.display = '';
            }
            return;
        }

        if (isTitle) {
            title.style.display = '';
        }
        list.replaceChildren(...items.map(createListItem));
    };

    const hasSearchResults = (data) => (
        (Array.isArray(data?.keywords) && data.keywords.length > 0)
        || (Array.isArray(data?.results) && data.results.length > 0)
    );

    const renderSuggestions = (data) => {
        if (!hasSearchResults(data)) {
            restoreFallbackSuggestions();
            setState('default');
            return;
        }

        renderList(keywordList, data.keywords, fallbackKeywords, false);
        renderList(suggestionList, data.results, fallbackSuggestions, false);
        setState('results');
    };

    const restoreFallbackSuggestions = () => {
        if (keywordList) {
            keywordList.innerHTML = fallbackKeywords;
        }

        if (suggestionList) {
            suggestionList.innerHTML = fallbackSuggestions;
        }
    };

    const requestSuggestions = () => {
        if (!suggestionsUrl || getSearchValue().length < MIN_SEARCH_LENGTH) {
            if (activeRequest) {
                activeRequest.abort();
                activeRequest = null;
            }
            window.clearTimeout(debounceTimer);
            restoreFallbackSuggestions();
            return;
        }

        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
            if (activeRequest) {
                activeRequest.abort();
            }

            const requestQuery = getSearchValue();
            activeRequest = new AbortController();
            const formData = new FormData();
            const antiForgeryToken = getAntiForgeryToken();

            formData.append('q', requestQuery);

            if (antiForgeryToken) {
                formData.append('__RequestVerificationToken', antiForgeryToken);
            }

            fetch(getSuggestionsUrl(), {
                method: 'POST',
                signal: activeRequest.signal,
                body: formData,
                headers: {
                    Accept: 'application/json',
                },
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('Search suggestions request failed');
                    }

                    return response.json();
                })
                .then((data) => {
                    if (requestQuery !== getSearchValue()) {
                        return;
                    }

                    renderSuggestions(data);
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        restoreFallbackSuggestions();
                        setState('default');
                    }
                });
        }, SEARCH_DEBOUNCE_DELAY);
    };

    const setState = (state) => {
        form.classList.toggle('is-warning', state === 'warning');
        form.classList.toggle('is-results', state === 'results');
    };

    const updateMenuState = (forceWarning = false) => {
        const searchValue = getSearchValue();

        if (searchValue.length >= MIN_SEARCH_LENGTH) {
            requestSuggestions();
            return;
        }

        restoreFallbackSuggestions();

        if (forceWarning || (form.classList.contains('is-warning') && searchValue.length > 0)) {
            setState('warning');
            return;
        }

        setState('default');
    };

    const openMenu = () => {
        if (mobileQuery.matches) {
            document.dispatchEvent(new CustomEvent('header:close-mobile-menu'));
            header?.classList.add('is-mobile-overlay-open');
            mobileOverlayShell?.setAttribute('aria-hidden', 'false');
            menuToggle?.setAttribute('aria-expanded', 'true');
            menuToggle?.setAttribute('aria-label', 'Menüyü kapat');
            document.body.classList.add('is-header-overlay-open');
        }

        form.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
        updateMenuState();
    };

    const closeMenu = () => {
        form.classList.remove('is-open');
        input.setAttribute('aria-expanded', 'false');
        setState('default');

        if (mobileQuery.matches) {
            header?.classList.remove('is-mobile-overlay-open');
            mobileOverlayShell?.setAttribute('aria-hidden', 'true');
            menuToggle?.setAttribute('aria-expanded', 'false');
            menuToggle?.setAttribute('aria-label', 'Menüyü aç');
            document.body.classList.remove('is-header-overlay-open');
        }
    };

    if (searchButton) {
        searchButton.addEventListener('click', (event) => {
            if (!mobileQuery.matches || form.classList.contains('is-open')) {
                return;
            }

            event.preventDefault();
            openMenu();
            input.focus();
        });
    }

    input.addEventListener('focus', openMenu);
    input.addEventListener('click', openMenu);
    input.addEventListener('input', () => {
        const cleaned = input.value.replace(DISALLOWED_SEARCH_CHARS, '');

        if (cleaned !== input.value) {
            const caret = input.selectionStart;
            const removed = input.value.length - cleaned.length;
            input.value = cleaned;
            const position = Math.max(0, caret - removed);
            input.setSelectionRange(position, position);
        }

        openMenu();
        updateMenuState();
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();

        if (getSearchValue().length < MIN_SEARCH_LENGTH) {
            updateMenuState(true);
            openMenu();
            return;
        }

        window.location.href = getSearchUrl();
    });

    if (showAllLink) {
        showAllLink.addEventListener('click', (event) => {
            if (getSearchValue().length < MIN_SEARCH_LENGTH) {
                event.preventDefault();
                updateMenuState(true);
                openMenu();
                return;
            }

            event.preventDefault();
            window.location.href = getSearchUrl();
        });
    }

    document.addEventListener('header:close-search', closeMenu);

    document.addEventListener('click', (event) => {
        if (mobileQuery.matches) {
            return;
        }

        if (!form.contains(event.target)) {
            closeMenu();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeMenu();
            input.blur();
        }
    });
});
